import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";

import PageChrome from "@/layouts/PageChrome.jsx";
import { Card, CardBody, CardExtra, CardHead, CardTitle } from "@/components/ui/Card.jsx";
import Button from "@/components/ui/Button.jsx";
import Icon from "@/components/ui/Icon.jsx";
import { SearchInput, Select } from "@/components/ui/Form.jsx";
import { AvatarStack, Callout, EmptyState, ProgressBar, StatCard, Tabs } from "@/components/ui/Display.jsx";
import Pagination, { PaginationInfo } from "@/components/ui/Pagination.jsx";
import { SortableTh, TableWrap, Td, Th, Tr } from "@/components/ui/Table.jsx";
import Tag from "@/components/ui/Tag.jsx";
import { useConfirm, useToast } from "@/components/ui/feedback.js";
import ProjectFormDrawer from "@/components/projects/ProjectFormDrawer.jsx";

import { projectService, teamService, userService } from "@/lib/api.js";
import { apiErrorMessage } from "@/lib/apiClient.js";
import { useAuth } from "@/context/auth.js";
import { formatDate, formatDuration } from "@/lib/format.js";
import { nextSort, sortRows } from "@/lib/sort.js";
import {
    budgetTone,
    filterProjects,
    hoursOf,
    idOf,
    isNearBudget,
    projectStats,
    sortAccessors,
    utilisation,
} from "@/lib/projects.js";

/*
 * Replaces frontend/app/(dashboard)/projects/page.js.
 *
 * The budget bar is the reason this page exists — "how many hours went where"
 * is unanswerable from the table it replaces, which lists names and member tags
 * only. Making it answerable took three backend changes: budget/code/billable
 * fields, TimeEntry.project becoming the reference it was always treated as,
 * and an hours aggregation endpoint. See backend/scripts/verify-project-hours.js.
 */

const NONE = [];

export default function Projects() {
    const queryClient = useQueryClient();
    const toast = useToast();
    const confirm = useConfirm();
    const { can } = useAuth();

    const [tab, setTab] = useState("active");
    const [search, setSearch] = useState("");
    const [team, setTeam] = useState("");
    const [nearBudget, setNearBudget] = useState(false);
    const [sort, setSort] = useState({ by: "hours", dir: "desc" });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [editing, setEditing] = useState(null);
    const [formError, setFormError] = useState("");

    const [projectsQuery, hoursQuery, teamsQuery, usersQuery] = useQueries({
        queries: [
            {
                queryKey: ["projects"],
                queryFn: () => projectService.getProjects().then((r) => r.data?.data?.projects ?? []),
            },
            {
                queryKey: ["project-hours"],
                queryFn: () => projectService.getProjectHours().then((r) => r.data?.data?.hours ?? {}),
            },
            {
                queryKey: ["teams"],
                queryFn: () =>
                    teamService.getOrganizationTeams().then((r) => r.data?.data?.teams ?? []),
            },
            {
                queryKey: ["users"],
                queryFn: () => userService.getAllUsers().then((r) => r.data?.data?.users ?? []),
            },
        ],
    });

    const projects = projectsQuery.data ?? NONE;
    const hours = hoursQuery.data ?? null;
    const teams = teamsQuery.data ?? NONE;
    const users = usersQuery.data ?? NONE;

    const stats = useMemo(() => projectStats(projects, hours), [projects, hours]);

    const rows = useMemo(() => {
        const matched = filterProjects(projects, { tab, search, team, nearBudget }, hours);
        return sortRows(matched, sort, sortAccessors(hours));
    }, [projects, tab, search, team, nearBudget, sort, hours]);

    const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
    const current = Math.min(page, totalPages);
    const visible = rows.slice((current - 1) * pageSize, current * pageSize);

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ["projects"] });
        queryClient.invalidateQueries({ queryKey: ["project-hours"] });
    };

    const saveProject = useMutation({
        mutationFn: (values) =>
            editing?._id
                ? projectService.updateProject(editing._id, values)
                : projectService.createProject(values),
        onSuccess: (_res, values) => {
            toast(`${values.name} ${editing?._id ? "updated" : "created"}`);
            setEditing(null);
            setFormError("");
            invalidate();
        },
        onError: (error) =>
            setFormError(apiErrorMessage(error, "Could not save this project. Please try again.")),
    });

    const setArchived = useMutation({
        mutationFn: ({ project, isArchived }) =>
            projectService.updateProject(project._id, { isArchived }),
        onSuccess: (_res, { project, isArchived }) => {
            toast(`${project.name} ${isArchived ? "archived" : "restored"}`);
            invalidate();
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not archive this project."), "error"),
    });

    const removeProject = useMutation({
        mutationFn: (project) => projectService.deleteProject(project._id),
        onSuccess: (_res, project) => {
            toast(`${project.name} deleted`, "error");
            invalidate();
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not delete this project."), "error"),
    });

    const askArchive = async (project) => {
        const archiving = !project.isArchived;
        const ok = await confirm({
            tone: archiving ? "warning" : "success",
            title: archiving ? `Archive ${project.name}?` : `Restore ${project.name}?`,
            body: archiving
                ? "It moves to the Archived tab and stops appearing when people pick a project. Time already logged against it is kept."
                : "It returns to the active list and can be picked again.",
            okLabel: archiving ? "Archive" : "Restore",
        });
        if (ok) setArchived.mutate({ project, isArchived: archiving });
    };

    const askDelete = async (project) => {
        const logged = hoursOf(project, hours);
        const ok = await confirm({
            tone: "danger",
            title: `Delete ${project.name}?`,
            body: (
                <>
                    <p>
                        The project is removed permanently.
                        {logged > 0 && (
                            <>
                                {" "}
                                <strong className="font-semibold text-slate-700 dark:text-slate-200">
                                    {formatDuration(logged)}
                                </strong>{" "}
                                already logged against it stays in the time entries but stops being
                                attributed to anything.
                            </>
                        )}
                    </p>
                    <p className="mt-1.5">
                        Archive it instead if you only want it out of the way.
                    </p>
                </>
            ),
            okLabel: "Delete project",
        });
        if (ok) removeProject.mutate(project);
    };

    const onSort = (field) => {
        setSort((s) => nextSort(s, field, "desc"));
        setPage(1);
    };

    const loading = projectsQuery.isPending;

    return (
        <>
            <PageChrome breadcrumb={["Projects"]} title="Projects">
                {can("manageUsers") && (
                    <Button
                        variant="primary"
                        onClick={() => {
                            setFormError("");
                            setEditing({});
                        }}
                        data-add-project
                    >
                        <Icon name="folder" className="size-4" />
                        New project
                    </Button>
                )}
            </PageChrome>

            <main className="space-y-5 p-4 sm:p-5">
                <div>
                    <h1 className="text-[22px] font-semibold tracking-tight">Projects</h1>
                    <p className="mt-0.5 text-[13px] text-slate-500 dark:text-slate-400">
                        What the hours are going into, and how that compares with the plan.
                    </p>
                </div>

                {projectsQuery.isError && (
                    <Callout tone="danger">
                        {apiErrorMessage(projectsQuery.error, "Failed to load projects.")}{" "}
                        <button
                            type="button"
                            onClick={() => projectsQuery.refetch()}
                            className="font-semibold underline"
                        >
                            Try again
                        </button>
                    </Callout>
                )}

                <section className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
                    <StatCard
                        tone="brand"
                        label="Active projects"
                        value={stats.active}
                        hint={`${stats.archived} archived`}
                        icon={<Icon name="folder" className="size-4" />}
                    />
                    <StatCard
                        tone="emerald"
                        label="Hours logged"
                        value={formatDuration(stats.totalMs)}
                        hint="Across active projects"
                        icon={<Icon name="clock" className="size-4" />}
                    />
                    <StatCard
                        tone="amber"
                        label="Near budget"
                        value={stats.nearBudget}
                        hint="85% or more of the planned hours"
                        icon={<Icon name="chart" className="size-4" />}
                        onClick={() => {
                            setNearBudget((v) => !v);
                            setPage(1);
                        }}
                        active={nearBudget}
                    />
                    <StatCard
                        tone="violet"
                        label="Billable"
                        value={stats.billable}
                        hint="Charged to a client"
                        icon={<Icon name="check" className="size-4" />}
                    />
                </section>

                <Card>
                    <CardHead>
                        <CardTitle>
                            <Tabs
                                value={tab}
                                onChange={(next) => {
                                    setTab(next);
                                    setPage(1);
                                }}
                                options={[
                                    { value: "active", label: `Active (${stats.active})` },
                                    { value: "archived", label: `Archived (${stats.archived})` },
                                ]}
                            />
                        </CardTitle>
                        <CardExtra>
                            <SearchInput
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPage(1);
                                }}
                                placeholder="Search name, code or description…"
                                aria-label="Search projects"
                                className="w-full sm:w-64"
                            />
                            <Select
                                size="sm"
                                value={team}
                                onChange={(e) => {
                                    setTeam(e.target.value);
                                    setPage(1);
                                }}
                                aria-label="Filter by team"
                                className="w-auto"
                            >
                                <option value="">All teams</option>
                                {teams.map((t) => (
                                    <option key={t._id} value={t._id}>
                                        {t.name}
                                    </option>
                                ))}
                            </Select>
                        </CardExtra>
                    </CardHead>

                    <CardBody>
                        {nearBudget && (
                            <div className="mb-3.5">
                                <Callout tone="warning">
                                    Showing only projects at 85% of their budget or beyond.{" "}
                                    <button
                                        type="button"
                                        onClick={() => setNearBudget(false)}
                                        className="font-semibold underline"
                                    >
                                        Show all
                                    </button>
                                </Callout>
                            </div>
                        )}

                        <TableWrap minWidth="1040px">
                            <thead>
                                <tr>
                                    <SortableTh field="name" sort={sort} onSort={onSort}>
                                        Project
                                    </SortableTh>
                                    <Th className="w-44">Teams</Th>
                                    <Th className="w-32">Members</Th>
                                    <SortableTh field="hours" sort={sort} onSort={onSort} className="w-64">
                                        Hours vs budget
                                    </SortableTh>
                                    <Th className="w-28">Billing</Th>
                                    <SortableTh field="created" sort={sort} onSort={onSort} className="w-40">
                                        Created
                                    </SortableTh>
                                    <Th last className="w-28 text-right">
                                        Actions
                                    </Th>
                                </tr>
                            </thead>

                            <tbody>
                                {visible.map((project) => (
                                    <ProjectRow
                                        key={project._id}
                                        project={project}
                                        hours={hours}
                                        hoursLoading={hoursQuery.isPending}
                                        canManage={can("manageUsers")}
                                        onEdit={() => {
                                            setFormError("");
                                            setEditing(project);
                                        }}
                                        onArchive={() => askArchive(project)}
                                        onDelete={() => askDelete(project)}
                                    />
                                ))}

                                {!visible.length && (
                                    <tr>
                                        <td colSpan={7}>
                                            <EmptyState
                                                title={
                                                    loading
                                                        ? "Loading…"
                                                        : tab === "archived"
                                                          ? "Nothing archived"
                                                          : projects.length
                                                            ? "No project matches these filters"
                                                            : "No projects yet"
                                                }
                                                hint={
                                                    tab === "archived"
                                                        ? "Archived projects keep their logged time and can be restored."
                                                        : "A project gives the tracked hours somewhere to go."
                                                }
                                            />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </TableWrap>

                        <div className="mt-3.5 flex flex-wrap items-center gap-3">
                            <PaginationInfo
                                page={current}
                                pageSize={pageSize}
                                total={rows.length}
                                noun="projects"
                                sizes={[10, 20, 50]}
                                onPageSizeChange={(n) => {
                                    setPageSize(n);
                                    setPage(1);
                                }}
                            />
                            <div className="ml-auto">
                                <Pagination page={current} totalPages={totalPages} onChange={setPage} />
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </main>

            {editing && (
                <ProjectFormDrawer
                    key={editing._id ?? "new"}
                    project={editing._id ? editing : null}
                    teams={teams}
                    users={users}
                    onClose={() => {
                        setEditing(null);
                        setFormError("");
                    }}
                    onSubmit={(values) => saveProject.mutate(values)}
                    saving={saveProject.isPending}
                    error={formError}
                />
            )}
        </>
    );
}

/* ---- One project --------------------------------------------------------- */
function ProjectRow({ project, hours, hoursLoading, canManage, onEdit, onArchive, onDelete }) {
    const logged = hoursOf(project, hours);
    const percent = utilisation(project, hours);
    const tone = budgetTone(percent);

    return (
        <Tr data-project={project._id}>
            <Td>
                <span className="min-w-0">
                    <span className="flex items-center gap-2">
                        <span className="truncate font-medium">{project.name}</span>
                        {project.code && (
                            <Tag tone="slate" className="font-mono">
                                {project.code}
                            </Tag>
                        )}
                        {isNearBudget(project, hours) && <Tag tone="amber">Near budget</Tag>}
                    </span>
                    <span className="mt-0.5 block max-w-md truncate text-[11.5px] text-slate-400 dark:text-slate-500">
                        {project.description || "No description provided"}
                    </span>
                </span>
            </Td>

            <Td>
                <span className="flex flex-wrap gap-1">
                    {(project.teams ?? []).length ? (
                        project.teams.map((t) => (
                            <Link
                                key={idOf(t)}
                                to="/teams"
                                className="tag bg-brand-50 text-brand-700 hover:bg-brand-100 dark:bg-brand-950 dark:text-brand-200"
                            >
                                {t.name ?? "Team"}
                            </Link>
                        ))
                    ) : (
                        <span className="text-[12px] text-slate-400 dark:text-slate-500">No team</span>
                    )}
                </span>
            </Td>

            <Td>
                {(project.members ?? []).length ? (
                    <span className="flex items-center gap-2">
                        <AvatarStack people={project.members} max={4} size="sm" />
                    </span>
                ) : (
                    <span className="text-[12px] text-slate-400 dark:text-slate-500">
                        Nobody assigned
                    </span>
                )}
            </Td>

            <Td>
                {hoursLoading ? (
                    <span className="block h-4 w-32 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
                ) : project.budgetHours ? (
                    <span className="flex items-center gap-2.5">
                        <span className="w-full min-w-24">
                            <ProgressBar percent={Math.min(100, percent)} tone={tone.bar} />
                        </span>
                        <span className="shrink-0 text-right">
                            <span className="block font-mono text-[12.5px] font-semibold">
                                {formatDuration(logged)}
                            </span>
                            <span className={`block font-mono text-[10.5px] ${tone.text}`}>
                                {Math.round(percent)}% of {project.budgetHours}h
                            </span>
                        </span>
                    </span>
                ) : (
                    <span className="flex items-baseline gap-1.5">
                        <span className="font-mono text-[12.5px] font-semibold">
                            {formatDuration(logged)}
                        </span>
                        <span className="text-[11.5px] text-slate-400 dark:text-slate-500">
                            no budget
                        </span>
                    </span>
                )}
            </Td>

            <Td>
                {project.billable ? <Tag tone="emerald">Billable</Tag> : <Tag tone="slate">Internal</Tag>}
            </Td>

            <Td className="text-slate-500 dark:text-slate-400">
                <span className="block text-[12.5px]">{formatDate(project.createdAt)}</span>
                <span className="block text-[11.5px] text-slate-400 dark:text-slate-500">
                    by {project.createdBy?.name ?? "Unknown"}
                </span>
            </Td>

            <Td last className="text-right">
                {canManage && (
                    <span className="flex items-center justify-end gap-0.5">
                        <Button
                            variant="icon"
                            aria-label={`Edit ${project.name}`}
                            onClick={onEdit}
                            data-edit={project._id}
                        >
                            <Icon name="cog" className="size-4" />
                        </Button>
                        <Button
                            variant="icon"
                            aria-label={`${project.isArchived ? "Restore" : "Archive"} ${project.name}`}
                            onClick={onArchive}
                            data-archive={project._id}
                        >
                            <Icon name={project.isArchived ? "arrowRight" : "folder"} className="size-4" />
                        </Button>
                        <Button
                            variant="icon"
                            aria-label={`Delete ${project.name}`}
                            onClick={onDelete}
                            data-delete={project._id}
                            className="text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40"
                        >
                            <Icon name="xCircle" className="size-4" />
                        </Button>
                    </span>
                )}
            </Td>
        </Tr>
    );
}
